import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Empty, Select, Switch, Tooltip, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { StepFormBranch, WorkflowField, WorkflowStep } from '@/api/workflows';
import { workflowsApi } from '@/api/workflows';
import { BmoModal } from '@/components/BmoModal';
import { ICON_BTN_PROPS } from '@/components/iconButton';
import { useFetchedList } from '@/hooks/useFetchedList';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';

type Props = {
  templateId: string;
  /** The step being configured; null hides the modal. */
  step: WorkflowStep | null;
  /** False when the workflow is locked by someone else or past Draft — read-only. */
  editable: boolean;
  onClose: () => void;
  onChanged: () => Promise<void> | void;
};

/** Every RadioGroup on this step, in document order. The only field type a branch
 *  can trigger off, mirroring the domain and `findOptionRedirect`'s scope: a
 *  single-pick control is the only one where "you have answered your way onto a
 *  different form" is unambiguous. */
function radioFieldsOf(step: WorkflowStep): WorkflowField[] {
  const out: WorkflowField[] = [];
  for (const section of step.sections ?? [])
    for (const row of section.rows ?? [])
      for (const col of row.columns ?? [])
        if (col.field && col.field.type === 'RadioGroup') out.push(col.field);
  return out;
}

/**
 * The step editor's "Advanced" panel, reached from the `…` in the Edit Step header.
 *
 * Holds the form-branch rules: "answer this radio this way and the rest of the
 * workflow becomes a different form". The inline sibling of the per-option redirect
 * in the field properties rail — a redirect navigates away to a brand-new
 * application, abandoning the answers already given, while a branch keeps the
 * application and swaps only the steps below this one.
 *
 * Draft-local until Save, like {@link ConditionsEditorModal}: the rules are a set
 * that only makes sense whole, so a half-edited row must not reach the wire because
 * the author paused. Save is one whole-state PUT.
 *
 * Deliberately NOT in `FieldPropertiesPanel`, where the redirect rule lives: that
 * panel's rules (never conditionally render a `Form.Item`; patch the whole `options`
 * array so `useWatch` fires) exist because it is one AntD form shared across every
 * field. A standalone modal over plain `useState` inherits neither.
 */
export function StepAdvancedModal({ templateId, step, editable, onClose, onChanged }: Props) {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');
  const open = step !== null;
  const [rows, setRows] = useState<StepFormBranch[]>([]);
  const [saving, setSaving] = useState(false);

  // Draft seeded on open and discarded on close, so Cancel is a true discard and
  // re-opening starts from what is stored rather than from the last edit.
  useEffect(() => {
    setRows(step ? (step.formBranches ?? []).map((b) => ({ ...b })) : []);
  }, [step]);

  const radioFields = useMemo(() => (step ? radioFieldsOf(step) : []), [step]);

  // Every workflow the originator could branch into. Drafts are included
  // deliberately and LABELLED as unpublished, for the reason OptionRedirectSection
  // spells out: two related forms are routinely authored in the same sprint, and
  // refusing to reference one until it is published makes the pair un-authorable.
  const { data: workflows, loading: workflowsLoading } = useFetchedList(
    () => workflowsApi.list(),
    t,
    'workflow.builder.properties.redirectWorkflowsLoadFailed',
  );
  const workflowChoices = useMemo(
    () =>
      (workflows ?? [])
        // A workflow cannot branch into itself: the runtime would splice the
        // branch step's own form in below it and re-resolve the same rule forever.
        .filter((w) => w.id !== templateId && !w.isArchived)
        .map((w) => {
          const name = (isFr && w.nameFr) || w.nameEn || w.appName;
          const live = w.isActive && w.currentVersionNumber > 0;
          return {
            value: w.id,
            label: live
              ? name
              : t('workflow.builder.properties.redirectWorkflowUnpublished', { name }),
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label)),
    [workflows, templateId, isFr, t],
  );

  const fieldChoices = radioFields.map((f) => ({
    value: f.id,
    label: (isFr && f.labelFr) || f.labelEn || f.id,
  }));

  const optionChoicesFor = (fieldId: string) =>
    (radioFields.find((f) => f.id === fieldId)?.options ?? []).map((o) => ({
      value: o.value,
      label: ((isFr && o.labelFr) || o.labelEn)?.trim() ? `${o.labelEn} (${o.value})` : o.value,
    }));

  const patch = (id: string, next: Partial<StepFormBranch>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...next } : r)));

  const addRow = () =>
    setRows((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        fieldId: radioFields[0]?.id ?? '',
        optionValue: '',
        targetTemplateId: '',
        appendFirstStep: false,
      },
    ]);

  // A half-filled row is not sent. The BE refuses an empty field / option / target,
  // and an author who added a row and walked away should not be blocked from saving
  // the rows they did finish — the same "collapse the half-authored rule to no rule"
  // rule the redirect payload builder follows.
  const complete = (r: StepFormBranch) => Boolean(r.fieldId && r.optionValue && r.targetTemplateId);

  const handleSave = async () => {
    if (!step) return;
    setSaving(true);
    try {
      await workflowsApi.setStepFormBranches(templateId, step.id, {
        formBranches: rows.filter(complete),
      });
      message.success(t('workflow.steps.advanced.saved'));
      await onChanged();
      onClose();
    } catch (err) {
      handleApiError(err, t, t('workflow.steps.advanced.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <BmoModal
      open={open}
      onCancel={onClose}
      width={720}
      title={t('workflow.steps.advanced.title')}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('common.cancel')}
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          disabled={!editable}
          onClick={handleSave}
        >
          {t('common.save')}
        </Button>,
      ]}
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        {t('workflow.steps.advanced.branchHint')}
      </Typography.Paragraph>

      {radioFields.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('workflow.steps.advanced.noRadioFields')}
        />
      ) : (
        <>
          {rows.length === 0 && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('workflow.steps.advanced.noBranches')}
            />
          )}
          {rows.map((row) => (
            <div
              key={row.id}
              data-testid={`form-branch-${row.id}`}
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                padding: 16,
                marginBottom: 12,
              }}
            >
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ display: 'grid', gap: 12, flex: 1 }}>
                  <label>
                    <div style={{ marginBottom: 4 }}>{t('workflow.steps.advanced.whenField')}</div>
                    <Select
                      style={{ width: '100%' }}
                      value={row.fieldId || undefined}
                      options={fieldChoices}
                      disabled={!editable}
                      placeholder={t('workflow.steps.advanced.whenFieldPlaceholder')}
                      onChange={(fieldId: string) =>
                        // Changing the field invalidates the answer picked from the
                        // old one — clear it rather than leave a value that names an
                        // option this field has never had.
                        patch(row.id, { fieldId, optionValue: '' })
                      }
                    />
                  </label>
                  <label>
                    <div style={{ marginBottom: 4 }}>{t('workflow.steps.advanced.whenOption')}</div>
                    <Select
                      style={{ width: '100%' }}
                      value={row.optionValue || undefined}
                      options={optionChoicesFor(row.fieldId)}
                      disabled={!editable || !row.fieldId}
                      placeholder={t('workflow.steps.advanced.whenOptionPlaceholder')}
                      onChange={(optionValue: string) => patch(row.id, { optionValue })}
                    />
                  </label>
                  <label>
                    <div style={{ marginBottom: 4 }}>{t('workflow.steps.advanced.loadForm')}</div>
                    <Select
                      style={{ width: '100%' }}
                      value={row.targetTemplateId || undefined}
                      options={workflowChoices}
                      loading={workflowsLoading}
                      disabled={!editable}
                      showSearch
                      optionFilterProp="label"
                      placeholder={t('workflow.steps.advanced.loadFormPlaceholder')}
                      onChange={(targetTemplateId: string) => patch(row.id, { targetTemplateId })}
                    />
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Switch
                      checked={row.appendFirstStep}
                      disabled={!editable}
                      onChange={(appendFirstStep) => patch(row.id, { appendFirstStep })}
                    />
                    <span>{t('workflow.steps.advanced.appendFirstStep')}</span>
                  </label>
                </div>
                <Tooltip title={t('workflow.steps.advanced.removeBranch')}>
                  <Button
                    {...ICON_BTN_PROPS}
                    danger
                    aria-label={t('workflow.steps.advanced.removeBranch')}
                    icon={<DeleteOutlined />}
                    disabled={!editable}
                    onClick={() => setRows((prev) => prev.filter((r) => r.id !== row.id))}
                  />
                </Tooltip>
              </div>
            </div>
          ))}

          {/* One rule per WORKFLOW is the convention, not a limit the BE enforces: BMO's
              save contract records a swap as a single boolean, so a resumed application
              carrying two possible branches has no way to say which one it was filling. */}
          {rows.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={t('workflow.steps.advanced.oneBranchNotice')}
            />
          )}

          <Button
            icon={<PlusOutlined />}
            onClick={addRow}
            disabled={!editable}
            data-testid="add-form-branch"
          >
            {t('workflow.steps.advanced.addBranch')}
          </Button>
        </>
      )}
    </BmoModal>
  );
}
